import { type AssetPart, type Part } from "@/entities/part/types";

// Bäckerei-spezifische Ersatzteil-Kategorien
const partCategories = [
  { name: "Heizelement", prefix: "HE", count: 15 },
  { name: "Dichtung", prefix: "DI", count: 20 },
  { name: "Filter", prefix: "FI", count: 15 },
  { name: "Backblech", prefix: "BB", count: 10 },
  { name: "Steuerung", prefix: "ST", count: 8 },
  { name: "Motor", prefix: "MO", count: 8 },
  { name: "Sensor", prefix: "SE", count: 12 },
  { name: "Kältekomponente", prefix: "KK", count: 10 },
  { name: "Pumpe", prefix: "PU", count: 8 },
  { name: "Kassenzubehör", prefix: "KZ", count: 6 },
];

// Detaillierte Ersatzteile für Bäckerei
const partDetails: { name: string; description: string; category: string }[] = [
  // Heizelemente
  {
    name: "Heizstab Oberhitze 2000W",
    description: "Heizelement für Etagenofen Oberhitze",
    category: "Heizelement",
  },
  {
    name: "Heizstab Unterhitze 1800W",
    description: "Heizelement für Etagenofen Unterhitze",
    category: "Heizelement",
  },
  {
    name: "Rundheizung Konvektomat 3000W",
    description: "Ringheizung für Konvektomat",
    category: "Heizelement",
  },
  {
    name: "Heizpatrone Dampferzeuger",
    description: "Heizpatrone für Dampfsystem",
    category: "Heizelement",
  },
  {
    name: "Heizstab Gärschrank 800W",
    description: "Heizelement für Gärschrank",
    category: "Heizelement",
  },
  // Dichtungen
  {
    name: "Türdichtung Etagenofen",
    description: "Silikonkautschuk-Dichtung für Backofentür",
    category: "Dichtung",
  },
  {
    name: "Türdichtung Konvektomat",
    description: "Hochtemperatur-Dichtung für Konvektomat",
    category: "Dichtung",
  },
  {
    name: "Pumpendichtung Spülmaschine",
    description: "O-Ring Set für Spülmaschinenpumpe",
    category: "Dichtung",
  },
  {
    name: "Kühltheken-Glasdichtung",
    description: "Dichtprofil für Kühlthekenglasscheibe",
    category: "Dichtung",
  },
  {
    name: "Tiefkühler-Türdichtung",
    description: "Magnetdichtung für Tiefkühltruhe",
    category: "Dichtung",
  },
  {
    name: "Gärschrank-Türdichtung",
    description: "Dichtung für Gärschranktür",
    category: "Dichtung",
  },
  // Filter
  {
    name: "Fettfilter Dunstabzug",
    description: "Edelstahl-Fettfilter für Dunstabzugshaube",
    category: "Filter",
  },
  {
    name: "Wasserfilter Kaffeemaschine",
    description: "Kalkfilter für Kaffeemaschine",
    category: "Filter",
  },
  {
    name: "Luftfilter Kompressor",
    description: "Luftfilter für Kältekompressor",
    category: "Filter",
  },
  {
    name: "Wasserfilter Spülmaschine",
    description: "Feinsieb für Spülmaschine",
    category: "Filter",
  },
  {
    name: "Dampferzeuger-Kalkfilter",
    description: "Wasseraufbereitungsfilter",
    category: "Filter",
  },
  // Backbleche
  {
    name: "Backblech 60x40 Alu",
    description: "Aluminium-Backblech Standardmaß",
    category: "Backblech",
  },
  {
    name: "Backblech 60x40 Teflon",
    description: "Antihaft-beschichtetes Backblech",
    category: "Backblech",
  },
  {
    name: "Lochblech 60x40",
    description: "Perforiertes Backblech für Brötchen",
    category: "Backblech",
  },
  { name: "Baguette-Blech 5er", description: "Rillenblech für 5 Baguettes", category: "Backblech" },
  // Steuerungen
  {
    name: "Zeitschaltuhr digital",
    description: "Digitale Zeitschaltuhr für Backofen",
    category: "Steuerung",
  },
  {
    name: "Temperaturregler",
    description: "Elektronischer Temperaturregler",
    category: "Steuerung",
  },
  {
    name: "Hauptplatine Konvektomat",
    description: "Steuerplatine für Rational-Gerät",
    category: "Steuerung",
  },
  {
    name: "Display-Modul Backofen",
    description: "Ersatz-Display für Bedienfeld",
    category: "Steuerung",
  },
  // Motoren
  {
    name: "Lüftermotor Konvektomat",
    description: "Umluftmotor für Konvektomat",
    category: "Motor",
  },
  { name: "Pumpenmotor Spülmaschine", description: "Ablaufpumpenmotor", category: "Motor" },
  { name: "Kompressormotor", description: "Ersatzmotor für Kühlkompressor", category: "Motor" },
  // Sensoren
  {
    name: "Temperaturfühler PT100",
    description: "Temperatursensor für Backofen",
    category: "Sensor",
  },
  { name: "Kerntemperaturfühler", description: "Einstechfühler für Brot", category: "Sensor" },
  {
    name: "Feuchtigkeitssensor",
    description: "Luftfeuchtemessung für Gärschrank",
    category: "Sensor",
  },
  {
    name: "Türkontaktschalter",
    description: "Türsensor für Sicherheitsabschaltung",
    category: "Sensor",
  },
  { name: "Niveausensor Wassertank", description: "Füllstandsensor", category: "Sensor" },
  // Kältekomponenten
  {
    name: "Kompressor R290",
    description: "Ersatzkompressor für Kühltheke",
    category: "Kältekomponente",
  },
  {
    name: "Verdampfer",
    description: "Ersatzverdampfer für Kühlgerät",
    category: "Kältekomponente",
  },
  {
    name: "Thermostat Tiefkühler",
    description: "Temperaturregler -18°C",
    category: "Kältekomponente",
  },
  { name: "Kältemittel R290 1kg", description: "Propan-Kältemittel", category: "Kältekomponente" },
  // Pumpen
  {
    name: "Wasserpumpe Dampferzeuger",
    description: "Hochdruckpumpe für Dampfsystem",
    category: "Pumpe",
  },
  { name: "Ablaufpumpe Spülmaschine", description: "Entwässerungspumpe", category: "Pumpe" },
  { name: "Dosierpumpe Reiniger", description: "Chemiedosierpumpe", category: "Pumpe" },
  // Kassenzubehör
  { name: "Bonrolle 80mm", description: "Thermopapier-Bonrolle 80x80", category: "Kassenzubehör" },
  { name: "Kassenschublade", description: "Ersatz-Geldschublade", category: "Kassenzubehör" },
  { name: "Barcodescanner", description: "Handscanner für EAN-Codes", category: "Kassenzubehör" },
];

const warehouseIds = ["wh-1", "wh-2", "wh-3"];

function generateParts(): Part[] {
  return partDetails.map((detail, index) => {
    const category = partCategories.find((c) => c.name === detail.category);
    const partNumber = `${category?.prefix || "XX"}-${String(index + 1).padStart(4, "0")}`;
    const stock = Math.floor(Math.random() * 25) + 1;
    const stockThresholdOrange = Math.floor(Math.random() * 8) + 5;
    const stockThresholdRed = Math.floor(stockThresholdOrange / 2);

    return {
      id: `part-${index + 1}`,
      accountId: "1",
      name: detail.name,
      description: detail.description,
      number: partNumber,
      partIdentifier: `PART-${Date.now().toString(36).toUpperCase()}${Math.random().toString(36).substring(2, 5).toUpperCase()}`,
      lotSize: Math.floor(Math.random() * 10) + 1,
      currentStock: stock,
      isDiscontinued: false,
      isNotifyLowStock: true,
      reorderPoint: stockThresholdOrange,
      safetyStock: stockThresholdRed,
      isNotReorder: false,
      warehouseId: warehouseIds[index % warehouseIds.length],
      storageLocation: `Regal ${Math.floor(Math.random() * 10) + 1}-${Math.floor(Math.random() * 5) + 1}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  });
}

/** Populated in mock mode by `seedAssetDerivedDataFromModels` from each asset model's compatible parts. */
export const mockParts = generateParts();
export const mockAssetParts: AssetPart[] = [];
